import { StandardDropdownPageError, StandardDropdownsService } from '@zaoblako/web/core/services/standard-dropdowns.service';
import { chunkIds } from '@zaoblako/web/shared/helpers/include-ids-pagination.helper';
import type { StandardDropdownDescriptorInterface } from '@zaoblako/shared/common/dtos/standard-dropdown/standard-dropdown-descriptor.dto';
import type { StandardDropdownItemInterface } from '@zaoblako/shared/common/interfaces/standard-dropdown-item.interface';

import { isNil } from 'lodash';
import { forkJoin, Subscription } from 'rxjs';

const SEARCH_DEBOUNCE_MS = 300;
/** One text line plus the dropdown row padding. Override with `virtualScrollItemSize`. */
export const STANDARD_DROPDOWN_VIRTUAL_ITEM_SIZE = 40;
const DESCRIPTOR_ERROR = 'Не загружен дескриптор';
const PAGE_LOAD_ERROR = 'Не удалось загрузить';
const PAGE_TRANSFORM_ERROR = 'Не удалось преобразовать список';

export interface StandardDropdownSource {
  customDescriptor?: StandardDropdownDescriptorInterface;
  descriptorApiUrl?: string;
  noCacheApiDescriptor?: boolean;
  additionalApiParams?: Record<string, any>;
}

/**
 * Shared descriptor loading, paging and key hydration for standard dropdowns.
 * Selection itself stays on the single/multi component.
 */
export class StandardDropdownEngine<T extends Record<string, any>> {
  public descriptor?: StandardDropdownDescriptorInterface = null;
  public values: T[] = [];
  public transformedItems: Record<string | number, StandardDropdownItemInterface> = {};
  public apiParams: Record<string, any> = {};
  public loading = false;
  public paginationLoading = false;
  public selectionLoading = false;
  public count = 0;
  public initInProgress = false;
  public initError?: string = null;
  /** Shown in the panel when a page request fails. Already loaded rows stay. */
  public listError?: string = null;
  /** Selected keys could not be loaded. The current selection stays. */
  public selectionError = false;
  /** True while pending keys are not yet written into the selection. */
  public keysApplied = true;
  /** Ignore a cleared selection until pending keys are applied. */
  public suppressEmit = false;

  /** Form selection that must stay in the list after a page replace. */
  public onPageReplaced?: () => void;
  /** Hydrated entities for pending keys. Must not emit to the parent. */
  public onKeysResolved?: (items: T[]) => void;

  private rawKeys: Array<string | number> | null = null;
  private pendingKeys: Array<string | number> = [];
  private additionalApiParams?: Record<string, any>;
  private descriptorSub?: Subscription;
  private pageSub?: Subscription;
  private hydrateSub?: Subscription;
  private scrollElement?: HTMLElement;
  private scrollListener?: () => void;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private appliedSearch?: string;

  public constructor(
    private readonly dropdowns: StandardDropdownsService,
    private readonly mark: () => void
  ) {}

  public get optionValue(): string | undefined {
    return this.descriptor?.properties?.key;
  }

  public get sort(): string | undefined {
    if (!this.descriptor?.properties?.sort?.length) {
      return undefined;
    }
    return this.descriptor.properties.sort.join(',');
  }

  public get filterBy(): string | undefined {
    if (!this.descriptor?.properties?.search?.length) {
      return undefined;
    }
    return this.descriptor.properties.search.join(',');
  }

  public get shouldIgnoreClearedSelection(): boolean {
    return this.suppressEmit || ((this.rawKeys?.length ?? 0) > 0 && !this.keysApplied);
  }

  public destroy = (): void => {
    this.clearSearchTimer();
    this.descriptorSub?.unsubscribe();
    this.pageSub?.unsubscribe();
    this.hydrateSub?.unsubscribe();
    this.unbindPanelScroll();
    this.transformedItems = {};
  };

  public configure = (source: StandardDropdownSource): void => {
    this.clearSearchTimer();
    this.appliedSearch = undefined;
    this.descriptorSub?.unsubscribe();
    this.pageSub?.unsubscribe();
    this.hydrateSub?.unsubscribe();
    this.descriptor = null;
    this.values = [];
    this.transformedItems = {};
    this.apiParams = {};
    this.count = 0;
    this.loading = false;
    this.paginationLoading = false;
    this.selectionLoading = false;
    this.initError = null;
    this.listError = null;
    this.selectionError = false;
    this.keysApplied = !this.rawKeys?.length;
    this.additionalApiParams = source.additionalApiParams;

    if (source.customDescriptor) {
      this.descriptor = source.customDescriptor;
      this.initInProgress = false;
      this.mark();
      this.loadFirstPage();
      return;
    }

    if (!source.descriptorApiUrl) {
      this.initError = 'Не настроено';
      this.initInProgress = false;
      this.mark();
      return;
    }

    this.initInProgress = true;
    this.mark();
    this.descriptorSub = this.dropdowns.getDescriptor(source.descriptorApiUrl, source.noCacheApiDescriptor).subscribe({
      next: (descriptor) => {
        this.descriptor = descriptor;
        this.initInProgress = false;
        this.mark();
        this.loadFirstPage();
      },
      error: () => {
        this.initError = DESCRIPTOR_ERROR;
        this.initInProgress = false;
        this.mark();
      }
    });
  };

  /**
   * Remember keys until the descriptor arrives, then load the matching entities.
   * `undefined` means the input is unbound and is ignored. `null` and `[]` clear the selection.
   */
  public setKeys = (keys: Array<string | number> | string | number | null | undefined): void => {
    if (keys === undefined) {
      return;
    }

    const list = (Array.isArray(keys) ? keys : isNil(keys) ? [] : [keys]).filter((key) => !isNil(key) && key !== '');
    if (this.sameRawKeys(list)) {
      return;
    }

    this.rawKeys = list;
    this.pendingKeys = this.descriptor ? this.castKeys(list) : [...list];
    this.keysApplied = false;
    if (this.descriptor && !this.initInProgress) {
      this.resolvePendingKeys();
    }
  };

  public search = (filter: string): void => {
    if (!this.descriptor?.filters) {
      return;
    }
    if (filter === this.appliedSearch && !this.searchTimer) {
      return;
    }

    this.clearSearchTimer();
    this.pageSub?.unsubscribe();
    this.pageSub = undefined;
    if (this.loading || this.paginationLoading) {
      this.loading = false;
      this.paginationLoading = false;
      this.mark();
    }

    this.searchTimer = setTimeout(() => {
      this.searchTimer = undefined;
      this.runSearch(filter);
    }, SEARCH_DEBOUNCE_MS);
  };

  public bindPanelScroll = (element?: HTMLElement | null): void => {
    this.unbindPanelScroll();
    if (!element) {
      return;
    }

    this.scrollElement = element;
    this.scrollListener = () => {
      if (element.offsetHeight + Math.ceil(element.scrollTop) >= element.scrollHeight - 1) {
        this.appendPage();
      }
    };
    element.addEventListener('scroll', this.scrollListener, { passive: true });
    this.scrollListener();
  };

  private runSearch = (filter: string): void => {
    if (!this.descriptor?.filters) {
      return;
    }

    this.appliedSearch = filter;
    this.hydrateSub?.unsubscribe();
    this.pageSub?.unsubscribe();
    this.loading = true;
    this.apiParams[this.descriptor.filters.search] = filter;
    this.apiParams[this.descriptor.filters.offset] = 0;
    this.mark();
    this.loadPage(this.apiParams, 'replace');
  };

  private clearSearchTimer = (): void => {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = undefined;
    }
  };

  public unbindPanelScroll = (): void => {
    if (this.scrollElement && this.scrollListener) {
      this.scrollElement.removeEventListener('scroll', this.scrollListener);
    }
    this.scrollElement = undefined;
    this.scrollListener = undefined;
  };

  public ensurePresent = (items: T[]): boolean => {
    const keyProperty = this.descriptor?.properties?.key;
    if (!keyProperty) {
      return false;
    }

    let changed = false;
    for (const item of items) {
      const key = item?.[keyProperty];
      if (isNil(key)) {
        continue;
      }
      if (!this.values.some((value) => value[keyProperty] === key)) {
        this.values = this.values.concat(item);
        changed = true;
      }
    }
    return changed;
  };

  public preloadMissing = (items: T[]): void => {
    const includeKeysFilter = this.descriptor?.filters?.includeKeys;
    const keyProperty = this.descriptor?.properties?.key;
    if (!includeKeysFilter || !keyProperty) {
      return;
    }

    const missing = items
      .map((item) => item?.[keyProperty] as string | number)
      .filter((key) => !isNil(key) && key !== '' && !this.transformedItems[key]);
    if (!missing.length) {
      return;
    }

    this.fetchKeys(this.castKeys(missing), false);
  };

  public keyOf = (value?: T | null): string | number | undefined => {
    const keyProperty = this.descriptor?.properties?.key;
    if (!keyProperty || isNil(value)) {
      return undefined;
    }
    const key = value[keyProperty] as string | number;
    return isNil(key) ? undefined : key;
  };

  public keysOf = (items: T[]): Array<string | number> => {
    return items.map((item) => this.keyOf(item)).filter((key): key is string | number => !isNil(key));
  };

  /** True when `items` are exactly the keys already applied from `selectedKey(s)`. */
  public matchesResolvedKeys = (items: T[]): boolean => {
    if (this.rawKeys === null || !this.keysApplied) {
      return false;
    }

    const resolved = this.castKeys(this.rawKeys);
    const next = this.keysOf(items);
    if (resolved.length !== next.length) {
      return false;
    }
    return resolved.every((key) => next.some((item) => String(item) === String(key)));
  };

  private mergeTransformedItems = (transformed: Record<string | number, StandardDropdownItemInterface>): void => {
    this.transformedItems = { ...this.transformedItems, ...transformed };
  };

  private loadFirstPage = (): void => {
    if (!this.descriptor?.filters) {
      return;
    }

    this.loading = true;
    this.apiParams = this.buildBaseParams();
    this.mark();
    this.loadPage(this.apiParams, 'replace');
  };

  private appendPage = (): void => {
    if (!this.descriptor?.filters || !this.descriptor.properties) {
      return;
    }
    if (this.values.length >= this.count || this.loading || this.paginationLoading) {
      return;
    }

    const offsetFilter = this.descriptor.filters.offset;
    const limitFilter = this.descriptor.filters.limit;
    this.paginationLoading = true;
    this.apiParams[offsetFilter] += this.apiParams[limitFilter];
    this.mark();
    this.loadPage(this.apiParams, 'append');
  };

  private loadPage = (params: Record<string, any>, mode: 'replace' | 'append'): void => {
    if (!this.descriptor) {
      return;
    }

    this.listError = null;
    this.pageSub?.unsubscribe();
    this.pageSub = this.dropdowns.loadPage<T>(this.descriptor, params).subscribe({
      next: (page) => {
        const keyProperty = this.descriptor?.properties?.key;
        if (mode === 'append' && keyProperty) {
          this.values = this.values.concat(page.items.filter((item) => !this.values.some((value) => value[keyProperty] === item[keyProperty])));
        } else {
          this.values = page.items;
        }
        this.mergeTransformedItems(page.transformed);
        this.count = page.count;
        this.loading = false;
        this.paginationLoading = false;
        if (mode === 'replace') {
          this.afterReplace();
        } else {
          this.scrollListener?.();
        }
        this.mark();
      },
      error: (error: unknown) => {
        this.failPage(error, mode);
        if (mode === 'replace' && !this.initError) {
          this.afterReplace();
        }
      }
    });
  };

  private failPage = (error: unknown, mode: 'replace' | 'append'): void => {
    this.loading = false;
    this.paginationLoading = false;
    if (this.isDescriptorError(error)) {
      this.initError = DESCRIPTOR_ERROR;
      this.mark();
      return;
    }
    if (mode === 'append') {
      this.rollbackAppendOffset();
    }
    this.listError = this.pageErrorMessage(error);
    this.mark();
  };

  private rollbackAppendOffset = (): void => {
    const offsetFilter = this.descriptor?.filters?.offset;
    const limitFilter = this.descriptor?.filters?.limit;
    if (!offsetFilter || !limitFilter) {
      return;
    }
    const limit = Number(this.apiParams[limitFilter] ?? 0);
    const offset = Number(this.apiParams[offsetFilter] ?? 0);
    this.apiParams[offsetFilter] = Math.max(0, offset - limit);
  };

  private pageErrorMessage = (error: unknown): string => {
    return error instanceof StandardDropdownPageError && error.kind === 'transform' ? PAGE_TRANSFORM_ERROR : PAGE_LOAD_ERROR;
  };

  private isDescriptorError = (error: unknown): boolean => {
    return error instanceof StandardDropdownPageError && error.kind === 'descriptor';
  };

  private afterReplace = (): void => {
    if (this.rawKeys) {
      this.resolvePendingKeys();
      return;
    }
    this.onPageReplaced?.();
  };

  private resolvePendingKeys = (): void => {
    if (!this.descriptor?.properties?.key || !this.rawKeys) {
      return;
    }

    this.selectionError = false;
    this.pendingKeys = this.castKeys(this.rawKeys);
    if (!this.pendingKeys.length) {
      this.publishResolved([]);
      return;
    }

    const keyProperty = this.descriptor.properties.key;
    const ready = this.pendingKeys.every((key) => this.values.some((item) => item[keyProperty] === key) && this.transformedItems[key]);
    if (ready) {
      this.publishResolved(this.itemsByKeys(this.pendingKeys));
      return;
    }

    this.selectionLoading = true;
    this.mark();
    this.fetchKeys(this.pendingKeys, true);
  };

  private fetchKeys = (keys: Array<string | number>, publish: boolean): void => {
    const descriptor = this.descriptor;
    const includeKeysFilter = descriptor?.filters?.includeKeys;
    if (!descriptor || !includeKeysFilter || !keys.length) {
      if (publish) {
        this.selectionLoading = false;
        this.selectionError = true;
        this.mark();
      }
      return;
    }

    this.hydrateSub?.unsubscribe();
    this.hydrateSub = forkJoin(
      chunkIds(keys).map((chunk) => {
        const params: Record<string, any> = {
          ...(this.additionalApiParams ?? {}),
          [includeKeysFilter]: chunk,
          [descriptor.filters.limit]: chunk.length,
          [descriptor.filters.offset]: 0
        };
        if (descriptor.filters.select && descriptor.select?.length) {
          params[descriptor.filters.select] = descriptor.select;
        }
        return this.dropdowns.loadPage<T>(descriptor, params);
      })
    ).subscribe({
      next: (pages) => {
        this.hydrateSub = undefined;
        const keyProperty = descriptor.properties.key;
        for (const page of pages) {
          this.values = this.values.concat(page.items.filter((item) => !this.values.some((value) => value[keyProperty] === item[keyProperty])));
          this.mergeTransformedItems(page.transformed);
        }
        if (publish) {
          this.publishResolved(this.itemsByKeys(this.pendingKeys));
          return;
        }
        this.mark();
      },
      error: (error: unknown) => {
        this.hydrateSub = undefined;
        this.selectionLoading = false;
        if (this.isDescriptorError(error)) {
          this.initError = DESCRIPTOR_ERROR;
          this.mark();
          return;
        }
        if (publish) {
          this.selectionError = true;
        } else {
          this.listError = this.pageErrorMessage(error);
        }
        this.mark();
      }
    });
  };

  private publishResolved = (items: T[]): void => {
    this.keysApplied = true;
    this.selectionLoading = false;
    this.suppressEmit = true;
    this.onKeysResolved?.(items);
    this.mark();
    setTimeout(() => {
      this.suppressEmit = false;
    });
  };

  private itemsByKeys = (keys: Array<string | number>): T[] => {
    const keyProperty = this.descriptor?.properties?.key;
    if (!keyProperty) {
      return [];
    }
    return keys
      .map((key) => this.values.find((item) => item[keyProperty] === key))
      .filter((item): item is T => !isNil(item));
  };

  private buildBaseParams = (): Record<string, any> => {
    const descriptor = this.descriptor;
    if (!descriptor?.filters || !descriptor.properties) {
      return { ...(this.additionalApiParams ?? {}) };
    }

    const params: Record<string, any> = {
      ...(this.additionalApiParams ?? {}),
      [descriptor.filters.limit]: descriptor.limit,
      [descriptor.filters.offset]: 0
    };
    if (descriptor.filters.select && descriptor.select?.length) {
      params[descriptor.filters.select] = descriptor.select;
    }
    if (descriptor.filters.sort && descriptor.properties.sort?.length) {
      params[descriptor.filters.sort] = descriptor.properties.sort.join(',');
    }
    return params;
  };

  private castKeys = (keys: Array<string | number>): Array<string | number> => {
    const keyKind = this.descriptor?.properties?.keyKind ?? 'number';
    return keys
      .filter((key) => !isNil(key) && key !== '')
      .map((key) => (keyKind === 'number' ? Number(key) : String(key)))
      .filter((key) => keyKind !== 'number' || !Number.isNaN(key as number));
  };

  private sameRawKeys = (keys: Array<string | number>): boolean => {
    const current = this.rawKeys ?? [];
    if (current.length !== keys.length) {
      return false;
    }
    return current.every((key, index) => key === keys[index]);
  };
}
